import React, { useEffect, useState } from 'react';
import { Modal, Form, Button, Spinner } from 'react-bootstrap';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { toast } from 'react-toastify';
import styles from './bodywon.module.css';
import { fetchCurrentUserProfile, updateCustomerProfile } from '../utils/profileApi';

const emptyForm = {
  firstName: '',
  lastName: '',
  email: '',
  username: '',
  password: '',
  confirmPassword: '',
  mobile: '',
};

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());

const EditAccountModal = ({ show, onHide, onUpdated }) => {
  const [form, setForm] = useState(emptyForm);
  const [meta, setMeta] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show) return;

    let cancelled = false;

    const loadProfile = async () => {
      setLoading(true);
      setError('');
      try {
        const profile = await fetchCurrentUserProfile();
        if (cancelled) return;
        setForm(profile.form);
        setMeta(profile.meta);
      } catch (err) {
        if (cancelled) return;
        setError(err.message || 'Could not load profile.');
        setForm(emptyForm);
        setMeta(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadProfile();

    return () => {
      cancelled = true;
    };
  }, [show]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const resetState = () => {
    setForm(emptyForm);
    setMeta(null);
    setError('');
    setShowPassword(false);
    setLoading(false);
    setSaving(false);
  };

  const handleHide = () => {
    resetState();
    onHide();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.firstName.trim()) {
      setError('First name is required.');
      return;
    }
    if (!form.lastName.trim()) {
      setError('Last name is required.');
      return;
    }
    if (!form.email.trim()) {
      setError('Email is required.');
      return;
    }
    if (!isValidEmail(form.email)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!form.username.trim()) {
      setError('Username is required.');
      return;
    }

    const password = form.password.trim();
    const confirmPassword = form.confirmPassword.trim();

    if (password || confirmPassword) {
      if (!password) {
        setError('Please enter a new password.');
        return;
      }
      if (password !== confirmPassword) {
        setError('Passwords do not match.');
        return;
      }
    }

    if (!meta?.userId) {
      setError('Profile could not be loaded. Please close and try again.');
      return;
    }

    setSaving(true);
    try {
      await updateCustomerProfile(form, meta);
      const username = form.username.trim();
      toast.success('Profile updated successfully.');
      onUpdated?.(username);
      handleHide();
    } catch (err) {
      setError(err.message || 'Could not update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={handleHide} centered className={styles.createAccountModal}>
      <Modal.Header closeButton>
        <Modal.Title className={styles.createAccountTitle}>Edit Account</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" role="status" />
            <div className="mt-2 text-muted" style={{ fontSize: '0.9rem' }}>Loading profile...</div>
          </div>
        ) : (
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Control
                type="text"
                name="firstName"
                placeholder="First Name *"
                className={styles.loginInput}
                value={form.firstName}
                onChange={handleChange}
                disabled={saving}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Control
                type="text"
                name="lastName"
                placeholder="Last Name *"
                className={styles.loginInput}
                value={form.lastName}
                onChange={handleChange}
                disabled={saving}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Control
                type="email"
                name="email"
                placeholder="Email *"
                className={styles.loginInput}
                value={form.email}
                onChange={handleChange}
                disabled={saving}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Control
                type="text"
                name="username"
                placeholder="Username *"
                className={styles.loginInput}
                value={form.username}
                onChange={handleChange}
                disabled={saving}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <div className={styles.passwordWrapper}>
                <Form.Control
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  placeholder="New Password (optional)"
                  className={styles.loginInput}
                  value={form.password}
                  onChange={handleChange}
                  disabled={saving}
                />
                <span
                  onClick={() => setShowPassword((value) => !value)}
                  className={styles.passwordToggle}
                >
                  {showPassword ? <FiEye /> : <FiEyeOff />}
                </span>
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <div className={styles.passwordWrapper}>
                <Form.Control
                  type={showPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  placeholder="Confirm New Password"
                  className={styles.loginInput}
                  value={form.confirmPassword}
                  onChange={handleChange}
                  disabled={saving}
                />
                <span
                  onClick={() => setShowPassword((value) => !value)}
                  className={styles.passwordToggle}
                >
                  {showPassword ? <FiEye /> : <FiEyeOff />}
                </span>
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Control
                type="tel"
                name="mobile"
                placeholder="Mobile Number (optional)"
                className={styles.loginInput}
                value={form.mobile}
                onChange={handleChange}
                disabled={saving}
              />
            </Form.Group>

            {error && (
              <div className={styles.createAccountError}>{error}</div>
            )}

            <Button
              variant="primary"
              type="submit"
              className={styles.loginButton}
              disabled={saving || !meta}
            >
              {saving ? 'Updating...' : 'Update Account'}
            </Button>
          </Form>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default EditAccountModal;
